
import {
  computed, defineComponent, onMounted, Ref, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import AppLoader from '@/components/UI/AppLoader.vue';
import {
  FOLDER_TYPES, MEDIA_TYPES, NAVIGATION_PAGES, UPLOAD_PLACES,
} from '@/types/enums';
import { useMainStore } from '@/store/mainStore';
import { IFileUpload, IImageCrop, ISearchLabelInput } from '@/types/interfaces';
import AppTakePhoto from '@/components/AppTakePhoto.vue';
import UseUpload from '@/composables/UseUpload';
import UploadService from '@/services/UploadService';
import { blobToUrl, getVideoThumbnail } from '@/helpers/videoThumbnail';
import isPhoto from '@/helpers/fileDetector';
import { convertBmpInFileList, bmpConvertorToFile } from '@/helpers/bmpConvertor';

export default defineComponent({
  components: {
    AppLoader,
    AppTakePhoto,
    // AppUploadInfo,
  },
  emits: ['add-case', 'close'],
  props: {
    show: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const router = useRouter();
    const route = useRoute();

    const { onUploadUpdate, uploadInfo } = UseUpload();

    const appTakePhoto = ref();

    const menuRef = ref();
    const fileInputDef = ref();

    // const filesSaving: Ref<boolean> = ref(false);
    const isSaving: Ref<boolean> = ref(false);

    const fileInputTUS: Ref<HTMLInputElement | null> = ref(null);
    const mediaToCrop = ref({} as IImageCrop);
    const mediaLoadedKey = ref(0);
    const mediaInputRef: Ref<HTMLInputElement | null> = ref(null);

    const currentPage = computed(() => route.meta.navigationPage);
    const showNewFolder = computed(() => driveStore?.currentFolder?.folderType !== FOLDER_TYPES.CASE_FOLDER);

    const loaderText = computed(() => {
      if (isSaving.value) {
        return 'Updating media';
      }
      return '';
    });

    function hidePopUp() {
      mainStore.BottomPopUpType = null;
    }

    function splitMedia(mediaList: FileList) {
      const fileList: File[] = [];
      const photoList: File[] = [];

      for (let i = 0; i < mediaList.length; i += 1) {
        if (isPhoto(mediaList[i])) {
          photoList.push(mediaList[i]);
        } else {
          fileList.push(mediaList[i]);
        }
      }

      return {
        fileList,
        photoList,
      };
    }
    async function uploadTUS(event: Event) {
      const mediaList: FileList | null = (event.target as HTMLInputElement).files;
      if (mediaList) {
        const { fileList, photoList } = splitMedia(mediaList);
        const newPhotoList = await convertBmpInFileList(photoList as unknown as FileList);
        const newFileList = await convertBmpInFileList(fileList as unknown as FileList);

        const fileUploads = UploadService.toFileUploads(newFileList);
        const photoUploads = UploadService.toFileUploads(newPhotoList);

        const folderId = currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER ? route.params.id as string : undefined;

        if (fileUploads.length) {
          UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: folderId ? UPLOAD_PLACES.FOLDER : UPLOAD_PLACES.MIXED, id: folderId }, folderId);
        }
        if (photoUploads.length) {
          UploadService.batchUploadTUS(photoUploads, onUploadUpdate, { location: folderId ? UPLOAD_PLACES.FOLDER_PHOTO : UPLOAD_PLACES.MIXED_PHOTO, id: folderId }, folderId);
        }

        hidePopUp();
      }
    }

    function addCase() {
      const path = currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER ? `/drive/case/create?folderId=${driveStore.currentFolder!.id}` : '/drive/case/create';
      router.push(path);
      // mainStore.openCreateCasePopUp();
      mainStore.BottomPopUpType = null;
    }

    function addFolder() {
      mainStore.openCreateFolderPopUp();
      mainStore.BottomPopUpType = null;
    }

    function addMedia(e: any) {
      const imgList: FileList = (e.target as HTMLInputElement).files!;

      [...imgList].forEach(async (img) => {
        let poster = '' as unknown | undefined | string;
        if (img.type.includes('video')) {
          const thumbnailBlob = await getVideoThumbnail(img, 1.0);
          poster = blobToUrl(thumbnailBlob);
        }

        setTimeout(() => {
          mediaToCrop.value = {
            url: URL.createObjectURL(img),
            fileName: img.name,
            fileType: img.type,
            poster,
          };
        });
        mediaLoadedKey.value += 1;
      });

      mediaInputRef.value!.value = '';
    }

    function handleMediaCancel() {
      mediaToCrop.value = {} as IImageCrop;
      appTakePhoto.value.resetAppTakePhoto();
    }

    function cancelUpload() {
      UploadService.isAbortUpload = true;
      mainStore.BottomPopUpType = null;
      handleMediaCancel();
    }

    function handleRetake() {
      mediaToCrop.value = {} as IImageCrop;
      (mediaInputRef!.value! as HTMLInputElement).click();
    }

    const labels = ref([] as ISearchLabelInput[]);
    async function handleLabels() {
      const response = await mainStore.onSearchInputLabels('');
      labels.value = response;
      return response;
    }

    function handleMultipleUpload(f: any) {
      const folderId = (route.params?.id as string) || undefined;

      const fileUploads = f.file as IFileUpload[];
      onUploadUpdate().setStartInfo(fileUploads);

      handleLabels()
        .then(() => UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: folderId ? UPLOAD_PLACES.FOLDER_PHOTO : UPLOAD_PLACES.MIXED_PHOTO, id: folderId }, folderId, mainStore.recentInputLabels, false));

      hidePopUp();
    }

    onClickOutside(menuRef, () => {
      if (props.show) {
        emit('close');
      }
    });

    onMounted(() => {
      handleLabels();
    });

    return {
      mediaInputRef,
      fileInputTUS,
      isSaving,
      loaderText,
      mediaToCrop,
      mediaLoadedKey,
      showNewFolder,
      appTakePhoto,
      labels,
      fileInputDef,
      uploadInfo,
      menuRef,
      hidePopUp,
      addCase,
      addFolder,
      addMedia,
      handleMediaCancel,
      handleRetake,
      uploadTUS,
      handleMultipleUpload,
      // uploadDefault,
      cancelUpload,
      MEDIA_TYPES,
      FOLDER_TYPES,
    };
  },
});
